import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { ArrowRight } from "lucide-react";

const industrialPipeline = "/images/industrial-pipeline.jpeg";

export default function About() {
  return (
    <div className="min-h-screen bg-gray-950">
      <SEOHead
        title="About Alkanes Engineering Limited - Engineering Company Lagos Nigeria"
        description="About Alkanes Engineering Limited - Leading engineering services provider for Oil & Gas, Power, Infrastructure in Lagos, Nigeria since 2009. Mission, values, expertise, team."
        keywords="Alkanes Engineering Limited, about engineering company, oil and gas company Nigeria, engineering firm Lagos, company mission"
        canonical="https://alkanesengineering.com/about"
      />
      <Header />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/35"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white">
            About Us
          </h1>
          <p className="text-lg text-gray-100 max-w-2xl">
            Learn more about Alkane Engineering Limited and our mission.
          </p>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="py-20 md:py-28 bg-gray-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
              Alkane Engineering at a Glance
            </h2>
            <div className="space-y-8">
              <div className="border-l-4 border-primary pl-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Who We Are
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Alkanes Engineering Limited is a Nigerian-incorporated, wholly
                  indigenous engineering company providing professional
                  engineering and construction services to the Oil & Gas, Power,
                  and Infrastructure sectors.
                </p>
              </div>

              <div className="border-l-4 border-primary pl-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Our Capabilities
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Our capabilities cover the full project lifecycle, including
                  engineering, procurement, construction, project coordination,
                  commissioning, and handover. We deliver practical, efficient
                  solutions tailored to the technical and operational
                  requirements of each sector.
                </p>
              </div>

              <div className="border-l-4 border-primary pl-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Our Approach
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  With strong local expertise and an in-depth understanding of
                  the Nigerian operating environment, we work closely with
                  clients and stakeholders to deliver safe, reliable, and
                  cost-effective projects that meet regulatory and
                  project-specific requirements.
                </p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mt-20">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
              Our Core Values
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700 hover:border-primary transition-all">
                <div className="w-12 h-12 bg-primary rounded-lg mb-4 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">🎯</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Excellence
                </h3>
                <p className="text-gray-300">
                  Commitment to delivering the highest quality engineering and
                  construction solutions.
                </p>
              </div>

              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700 hover:border-primary transition-all">
                <div className="w-12 h-12 bg-primary rounded-lg mb-4 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">🛡️</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Safety
                </h3>
                <p className="text-gray-300">
                  Unwavering dedication to health, safety, and environmental
                  standards.
                </p>
              </div>

              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700 hover:border-primary transition-all">
                <div className="w-12 h-12 bg-primary rounded-lg mb-4 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">🤝</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Integrity
                </h3>
                <p className="text-gray-300">
                  Honest, transparent, and ethical conduct in all business
                  dealings.
                </p>
              </div>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mt-20">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
              Why Choose Alkane Engineering
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Local Expertise
                  </h3>
                  <p className="text-gray-300">
                    Deep understanding of Nigerian operating environment and
                    regulatory landscape
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Proven Track Record
                  </h3>
                  <p className="text-gray-300">
                    Successful delivery of complex projects across Oil & Gas,
                    Power, and Infrastructure
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Full Lifecycle Services
                  </h3>
                  <p className="text-gray-300">
                    End-to-end solutions from engineering design through
                    commissioning
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Client-Centric Approach
                  </h3>
                  <p className="text-gray-300">
                    Tailored solutions that meet your specific project
                    requirements and goals
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Leadership Team */}
          <section className="mt-20" aria-labelledby="team-heading">
            <div className="mb-10 max-w-3xl">
              <h2
                id="team-heading"
                className="text-3xl md:text-4xl font-bold text-white"
              >
                Our Team
              </h2>
              <p className="mt-4 text-gray-300 text-lg leading-relaxed">
                Meet the experienced professionals helping guide our engineering
                and project delivery.
              </p>
            </div>

            <article className="overflow-hidden rounded-2xl border border-gray-700 bg-gray-800 shadow-xl">
              <div className="grid md:grid-cols-[280px_1fr]">
                <div className="flex min-h-64 flex-col items-center justify-center bg-gradient-to-br from-primary to-blue-950 p-8 text-center">
                  <div
                    className="flex h-32 w-32 items-center justify-center rounded-full border-4 border-white/20 bg-white/10 text-4xl font-bold tracking-wide text-white shadow-lg"
                    aria-label="Hersh Sood initials"
                  >
                    HS
                  </div>
                  <p className="mt-5 text-xl font-bold text-white">
                    Hersh Sood
                  </p>
                  <p className="mt-1 text-sm font-semibold text-blue-100">
                    Technical Consultant
                  </p>
                  <a
                    href="mailto:hersh.sood@alkanesengineering.com"
                    className="mt-3 block w-full max-w-full break-all text-[10px] leading-4 tracking-tight text-blue-100 underline-offset-4 hover:underline"
                  >
                    hersh.sood@alkanesengineering.com
                  </a>
                  <p className="mt-3 w-full break-words text-center text-[10px] leading-4 tracking-tight text-blue-100">
                    <a className="hover:underline" href="tel:+18328193243">
                      +1-832-819-3243
                    </a>
                    {", "}
                    <a className="hover:underline" href="tel:+18328571433">
                      +1-832-857-1433
                    </a>
                    {", "}
                    <a className="hover:underline" href="tel:+50375211043">
                      +503 7521 1043
                    </a>
                  </p>
                </div>

                <div className="p-6 md:p-10">
                  <p className="text-gray-300 leading-relaxed">
                    Facilities Planning, Design and Development, specializing in
                    Oil, Gas, and Energy Infrastructure build-out aligned with
                    the Operations and Maintenance of critical production
                    equipment.
                  </p>

                  <div className="mt-8 border-t border-gray-700 pt-6">
                    <h4 className="text-lg font-semibold text-white">About</h4>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                      Commercial and Technical Engineer, Lead, and Manager
                      articulating ideas to improve operations for capital-intensive
                      and competitive upstream, midstream, refining, and chemical
                      businesses. With a hands-on approach and a track record of
                      achievement, Hersh has developed his experience and honed
                      his skill set while delivering projects efficiently and
                      economically in some of the world’s most challenging
                      commercial, operational, environmental, logistics, and
                      regulatory conditions. He has completed AI certification in
                      Project Management.
                    </p>
                  </div>
                </div>
              </div>
            </article>
          </section>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl md:text-4xl font-bold">
            Ready to work with us?
          </h2>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-primary px-8 py-3 rounded-lg font-semibold transition-all hover:shadow-lg hover:-translate-y-1"
          >
            Get Started <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
